import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { StateService } from '@adgate/common/services/state-service';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { RESTApplicationResponse } from '../types/rest-application-response.type';

// Shared refresh observable to prevent multiple simultaneous refresh calls
let refreshTokenInProgress$: Observable<RESTApplicationResponse> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) =>
{
  const authService = inject(AuthenticationService);
  const stateService = inject(StateService);
  const token = stateService.data.userToken;

  // For refresh token endpoint: add headers but skip token status check to avoid circular dependency
  if (req.url.includes('/auth/refreshAuthenticationToken')) {
    let newReq = req.clone({
      headers: buildHeaders(token)
    });
    return next(newReq);
  }

  if( token ) {
    const tokenStatus = authService.getTokenStatus(token);
    if( tokenStatus === authService.TOKENSTATUS_EXPIRED ) {
      authService.signOut();
      return throwError(() => new Error("Your session has expired. Please log in again."));
    } else if( tokenStatus === authService.TOKENSTATUS_NEEDSREFRESH ) {
      // Double-checked locking: if refresh already in progress, wait for it
      if (!refreshTokenInProgress$) {
        // Start refresh and share it among all waiting requests
        refreshTokenInProgress$ = authService.refreshAuthenticationToken().pipe(
          switchMap((r: RESTApplicationResponse) => {
            refreshTokenInProgress$ = null;
            return of(r);
          })
        );
      }

      // Wait for refresh to complete, then proceed with updated token
      return refreshTokenInProgress$.pipe(
        switchMap((r: RESTApplicationResponse) => {
          let newReq = req.clone({
            headers: buildHeaders(stateService.data.userToken)
          });

          return next(newReq);
        })
      );
    } else {
      let newReq = req.clone({
        headers: buildHeaders(token)
      });

      return next(newReq);
    }

  } else {
    let newReq = req.clone({
      headers: buildHeaders(null)
    });

    return next(newReq);
  }
};

function buildHeaders(token: string | null): HttpHeaders
{
  const headers: HttpHeaders = new HttpHeaders({
    'x-auth-token': atob(environment.idHash),
    'x-user-token': (token != null ? token : '')
  });

  return headers;
}
